import * as THREE from "three/webgpu";

const TAU = Math.PI * 2;
const STAR_SPRITE_SIZE = 64;

type PopulationConfig = {
  count: number;
  radiusMin: number;
  radiusMax: number;
  size: number;
  opacity: number;
  temperatureBias: number;
};

const POPULATIONS: PopulationConfig[] = [
  { count: 520, radiusMin: 66, radiusMax: 84, size: 0.05, opacity: 0.3, temperatureBias: 0.2 },
  { count: 360, radiusMin: 82, radiusMax: 93, size: 0.034, opacity: 0.22, temperatureBias: 0.62 },
  { count: 180, radiusMin: 92, radiusMax: 98, size: 0.022, opacity: 0.13, temperatureBias: 0.82 },
];

export class ProceduralCelestialEnvironment {
  private readonly root = new THREE.Group();
  private readonly populations: THREE.Points[] = [];
  private readonly dustLane: THREE.Points;
  private readonly distantGalaxies: THREE.Points;
  private readonly starSprite: THREE.DataTexture;
  private accessibleMode = false;
  private reducedMotion = false;

  constructor(scene: THREE.Scene) {
    scene.add(this.root);
    this.starSprite = createStarSpriteTexture();
    POPULATIONS.forEach((config, index) => this.populations.push(this.createPopulation(config, index + 1)));
    for (const population of this.populations) this.root.add(population);
    this.dustLane = this.createDustLane();
    this.distantGalaxies = this.createDistantGalaxies();
    this.root.add(this.dustLane, this.distantGalaxies);
    this.root.rotation.set(0.08, -0.12, 0.02);
  }

  setAccessibleMode(enabled: boolean) {
    this.accessibleMode = enabled;
    this.applyOpacityPolicy();
  }

  setReducedMotion(reduced: boolean) {
    this.reducedMotion = reduced;
  }

  update(delta: number, elapsed: number, idleMode: boolean) {
    if (this.reducedMotion || this.accessibleMode) return;
    const drift = idleMode ? 1 : 0.32;
    this.root.rotation.y += delta * 0.00042 * drift;
    this.root.rotation.x = 0.08 + Math.sin(elapsed * 0.025) * 0.004 * drift;
  }

  dispose(scene: THREE.Scene) {
    scene.remove(this.root);
    this.starSprite.dispose();
    for (const population of [...this.populations, this.dustLane, this.distantGalaxies]) {
      population.geometry.dispose();
      (population.material as THREE.PointsMaterial).dispose();
    }
  }

  private createPopulation(config: PopulationConfig, seedOffset: number) {
    const positions = new Float32Array(config.count * 3);
    const colors = new Float32Array(config.count * 3);
    const color = new THREE.Color();
    for (let index = 0; index < config.count; index += 1) {
      const u = deterministic(index * 3 + seedOffset * 97);
      const v = deterministic(index * 5 + seedOffset * 131);
      const w = deterministic(index * 7 + seedOffset * 173);
      const radius = config.radiusMin + (config.radiusMax - config.radiusMin) * w;
      const theta = u * TAU;
      const phi = Math.acos(2 * v - 1);
      const offset = index * 3;
      positions[offset] = radius * Math.sin(phi) * Math.cos(theta);
      positions[offset + 1] = radius * Math.cos(phi);
      positions[offset + 2] = radius * Math.sin(phi) * Math.sin(theta);

      const temperature = Math.max(
        0,
        Math.min(1, config.temperatureBias + (deterministic(index * 11 + seedOffset * 211) - 0.5) * 0.36),
      );
      color.setRGB(0.62 + temperature * 0.3, 0.72 + temperature * 0.2, 0.86 + (1 - temperature) * 0.12);
      colors[offset] = color.r;
      colors[offset + 1] = color.g;
      colors[offset + 2] = color.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: config.size,
      opacity: config.opacity,
      transparent: true,
      alphaTest: 0.12,
      depthWrite: false,
      depthTest: false,
      vertexColors: true,
      sizeAttenuation: true,
      toneMapped: false,
      map: this.starSprite,
      alphaMap: this.starSprite,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.userData.baseOpacity = config.opacity;
    return points;
  }

  private createDustLane() {
    const count = 320;
    const positions = new Float32Array(count * 3);
    for (let index = 0; index < count; index += 1) {
      const radius = 76 + deterministic(index * 17 + 31) * 20;
      const angle = deterministic(index * 19 + 47) * TAU;
      const offset = index * 3;
      positions[offset] = Math.cos(angle) * radius;
      positions[offset + 1] = (deterministic(index * 23 + 59) - 0.5) * 14;
      positions[offset + 2] = Math.sin(angle) * radius;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      size: 0.028,
      color: 0x7890b4,
      opacity: 0.06,
      transparent: true,
      alphaTest: 0.12,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
      map: this.starSprite,
      alphaMap: this.starSprite,
      blending: THREE.NormalBlending,
    });
    const points = new THREE.Points(geometry, material);
    points.rotation.z = -0.18;
    points.userData.baseOpacity = 0.06;
    return points;
  }

  private createDistantGalaxies() {
    const count = 72;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const color = new THREE.Color();
    for (let index = 0; index < count; index += 1) {
      const cluster = Math.floor(index / 12);
      const arm = index % 12;
      const baseAngle = cluster * 1.03;
      const radius = 89 + cluster * 1.25 + arm * 0.16;
      const spread = (deterministic(index * 29 + 71) - 0.5) * 7;
      const offset = index * 3;
      positions[offset] = Math.cos(baseAngle + arm * 0.035) * radius + spread;
      positions[offset + 1] = -42 + cluster * 16 + (deterministic(index * 31 + 83) - 0.5) * 6;
      positions[offset + 2] = Math.sin(baseAngle + arm * 0.035) * radius + spread;
      color.setRGB(0.5 + cluster * 0.035, 0.56 + cluster * 0.025, 0.72 + cluster * 0.02);
      colors[offset] = color.r;
      colors[offset + 1] = color.g;
      colors[offset + 2] = color.b;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.085,
      opacity: 0.09,
      transparent: true,
      alphaTest: 0.1,
      depthWrite: false,
      depthTest: false,
      vertexColors: true,
      toneMapped: false,
      map: this.starSprite,
      alphaMap: this.starSprite,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geometry, material);
    points.userData.baseOpacity = 0.09;
    return points;
  }

  private applyOpacityPolicy() {
    for (const population of [...this.populations, this.dustLane, this.distantGalaxies]) {
      const material = population.material as THREE.PointsMaterial;
      const base = typeof population.userData.baseOpacity === "number" ? population.userData.baseOpacity : 0.2;
      material.opacity = this.accessibleMode ? base * 0.42 : base;
    }
  }
}

function createStarSpriteTexture() {
  const data = new Uint8Array(STAR_SPRITE_SIZE * STAR_SPRITE_SIZE * 4);
  const center = (STAR_SPRITE_SIZE - 1) / 2;
  const radius = STAR_SPRITE_SIZE * 0.5;

  for (let y = 0; y < STAR_SPRITE_SIZE; y += 1) {
    for (let x = 0; x < STAR_SPRITE_SIZE; x += 1) {
      const dx = (x - center) / radius;
      const dy = (y - center) / radius;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.max(0, 1 - distance);
      const alpha = Math.pow(falloff, 2.2);
      const core = Math.pow(Math.max(0, 1 - distance * 1.35), 6);
      const luminance = Math.max(alpha * 0.9, core);
      const offset = (y * STAR_SPRITE_SIZE + x) * 4;
      const channel = Math.round(255 * luminance);
      data[offset] = channel;
      data[offset + 1] = channel;
      data[offset + 2] = channel;
      data[offset + 3] = Math.round(255 * alpha);
    }
  }

  const texture = new THREE.DataTexture(data, STAR_SPRITE_SIZE, STAR_SPRITE_SIZE, THREE.RGBAFormat);
  texture.needsUpdate = true;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipMapLinearFilter;
  texture.generateMipmaps = true;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
}

function deterministic(seed: number) {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}
